// src/screens/EditarCargoScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoWalletOutline, IoAlertCircleOutline } from 'react-icons/io5';
import moment from 'moment';
import './CrearCargoScreen.css'; // Reuse form styles
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function EditarCargoScreen() {
  const navigate = useNavigate();
  const { cargoId } = useParams();
  const { userData } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Estados del formulario
  const [residenteNombre, setResidenteNombre] = useState('');
  const [tipo, setTipo] = useState('Cuota Ordinaria');
  const [descripcion, setDescripcion] = useState('');
  const [monto, setMonto] = useState(0);
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [estatus, setEstatus] = useState('pendiente');

  // Modal de éxito
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Fixed gradient for the banner (Cyan)
  const gradient = 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)';

  useEffect(() => {
    if (!cargoId || !userData) return;

    const fetchCargo = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "cargos", cargoId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.condominioId !== userData.condominioId) {
            setError("No tienes permiso para editar este cargo.");
            return;
          }
          
          // ¡Tu regla de negocio!
          if (data.estatus === 'pagado') {
            setError("Este cargo ya fue pagado y no puede editarse.");
            // Caemos en modo "solo lectura"
          }

          setResidenteNombre(data.nombreResidente);
          setTipo(data.tipo);
          setDescripcion(data.descripcion || '');
          setMonto(data.monto);
          // Convertir Timestamp a formato YYYY-MM-DD para el input[type=date]
          setFechaVencimiento(moment(data.fechaVencimiento.toDate()).format('YYYY-MM-DD'));
          setEstatus(data.estatus);

        } else {
          setError("No se encontró el cargo.");
        }
      } catch (e) {
        setError("Error al cargar datos.");
        console.error(e);
      }
      setLoading(false);
    };

    fetchCargo();
  }, [cargoId, userData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (estatus === 'pagado') {
        setError("No se puede guardar un cargo que ya está pagado.");
        return;
    }
    
    setLoading(true);
    setError('');

    try {
      const docRef = doc(db, "cargos", cargoId);
      
      await updateDoc(docRef, {
        tipo: tipo,
        descripcion: descripcion,
        monto: Number(monto),
        fechaVencimiento: Timestamp.fromDate(new Date(fechaVencimiento + 'T12:00:00')), // Noon to avoid timezone shifts
      });

      setShowSuccessModal(true);

    } catch (e) {
      console.error("Error al actualizar cargo: ", e);
      setError("Error al guardar los cambios.");
      setLoading(false);
    }
  };

  const isReadOnly = estatus === 'pagado' || loading;

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-cuotas" />
      
      <main className="ks-main">
        <KondoBrandHeader title="Administración" showBackButton={false} />
        
        <div className="ks-scroll-area">
          <div className="crear-cargo-container">
            {/* Premium Header Banner */}
            <div style={{
              background: gradient || 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
              position: 'relative',
              overflow: 'hidden',
              padding: '25px',
              borderRadius: '15px',
              marginBottom: '30px',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', zIndex: 2, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <button
                    onClick={() => navigate('/gestion-cuotas')}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(5px)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '42px',
                      height: '42px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      color: 'white',
                      flexShrink: 0
                    }}
                  >
                    <IoArrowBack size={22} />
                  </button>
                  <div style={{ marginTop: '0' }}>
                    <h1 style={{ color: 'white', margin: '0 0 5px', fontSize: '1.6rem', fontWeight: '800' }}>
                      Editar Cargo
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.9rem', fontWeight: '500' }}>
                      Modifica la configuración de este cobro.
                    </p>
                  </div>
                </div>
              </div>
              <div style={{ position: 'absolute', right: '-15px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.15 }}>
                <IoWalletOutline size={150} color="white" />
              </div>
            </div>

            <div className="cc-form-wrapper">
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                {error && <div style={{ color: '#ef4444', backgroundColor: '#fee2e2', padding: '12px', borderRadius: '8px', fontSize: '0.9em' }}>{error}</div>}

                <div className="cc-form-group">
                  <label>Residente</label>
                  <input type="text" value={residenteNombre} className="cc-input" disabled style={{ backgroundColor: '#f1f5f9', color: '#64748b' }} />
                </div>

                <div className="cc-form-group">
                  <label>Tipo de Cargo</label>
                  <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="cc-select" required disabled={isReadOnly}>
                    <option value="Cuota Ordinaria">Cuota Ordinaria</option>
                    <option value="Cuota Extraordinaria">Cuota Extraordinaria</option>
                    <option value="Multa">Multa</option>
                    <option value="Intereses">Intereses</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div className="cc-form-group">
                  <label>Descripción (Opcional)</label>
                  <input type="text" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} className="cc-input" disabled={isReadOnly} placeholder="Ej: Mantenimiento Enero 2026" />
                </div>

                <div className="cc-form-group">
                  <label>Monto (MXN)</label>
                  <input type="number" value={monto} onChange={(e) => setMonto(e.target.value)} className="cc-input" min="0.01" step="0.01" required disabled={isReadOnly} />
                </div>
                
                <div className="cc-form-group">
                  <label>Fecha de Vencimiento</label>
                  <input type="date" value={fechaVencimiento} onChange={(e) => setFechaVencimiento(e.target.value)} className="cc-input" required disabled={isReadOnly} />
                </div>
                
                {!isReadOnly && (
                  <button type="submit" className="cc-btn-primary" disabled={loading}>
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      </main>

      {showSuccessModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 99999
        }}>
          <div style={{
            background: 'white', padding: '30px', borderRadius: '24px',
            width: '90%', maxWidth: '380px', textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            fontFamily: "'Inter', sans-serif"
          }}>
            <div style={{
              width: '60px', height: '60px', borderRadius: '50%',
              backgroundColor: '#e0f2fe', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', color: '#0284c7', fontSize: '28px',
              boxShadow: '0 4px 10px rgba(2, 132, 199, 0.1)'
            }}>
              ✨
            </div>
            <h3 style={{ margin: '0 0 10px', fontSize: '1.3rem', fontWeight: '800', color: '#1e293b' }}>
              ¡Cobro Actualizado!
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '0.95rem', color: '#64748b', lineHeight: '1.5' }}>
              Los cambios del cargo para <strong>{residenteNombre}</strong> se han guardado con éxito.
            </p>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/gestion-cuotas');
              }}
              style={{
                width: '100%', padding: '12px 24px', border: 'none',
                borderRadius: '12px', background: '#3b82f6', color: 'white',
                fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                transition: 'all 0.2s'
              }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      <KondoBottomNav activeRoute="/gestion-cuotas" />
    </div>
  );
}

export default EditarCargoScreen;